import React, { useState } from 'react'
import styled, { RuleSet } from 'styled-components'
import Skeleton from 'react-loading-skeleton'
import useIntersection from 'src/hooks/useIntersectionHandler.hook'
import coupon_thumbnail_default from 'src/assets/images/coupon_thumbnail/coupon_thumbnail_default.png'
import { Container } from 'src/components/common/Container'
import RenderIf from 'src/components/common/RenderIf'

interface LazyImageProps {
  src?: string
  style?: RuleSet<object>
  alt?: string
  onClick?: () => void
}

const LazyImage = React.memo(
  ({ src, alt, style, onClick }: LazyImageProps) => {
    const [imageOnLoad, setImageOnLoad] = useState(false)

    const ref = useIntersection(
      ({ target }, observer) => {
        const imageSrc = target.getAttribute('data-src')

        if (imageSrc) {
          target.removeAttribute('data-src')
          target.setAttribute('src', imageSrc)
          observer.unobserve(target)
        }
      },
      {
        rootMargin: '100px',
        threshold: 0.0,
      },
    )

    const onImageErrorHandler = (e: React.SyntheticEvent<HTMLImageElement, Event>) =>
      (e.currentTarget.src = coupon_thumbnail_default)

    const load = !!src && imageOnLoad

    return (
      <LazyImageContainer $option="flex_column" $style={style}>
        <LazyImageStyle
          data-src={src}
          ref={ref}
          onClick={onClick}
          onError={onImageErrorHandler}
          $load={load}
          onLoad={() => setImageOnLoad(true)}
          alt={alt || '...loading'}
        />
        <RenderIf when={!load}>
          <SkeletonComponent $style={style} />
        </RenderIf>
      </LazyImageContainer>
    )
  },
  (prevProps, nextProps) => {
    return prevProps.src === nextProps.src && prevProps.alt === nextProps.alt
  },
)

const LazyImageStyle = styled.img<{ $load?: boolean }>`
  position: relative;
  font-size: 0;
  top: 0;
  visibility: ${(props) => (props.$load ? 'visible' : 'hidden')};
  height: 100%;
`
const SkeletonComponent = styled(Skeleton)<{ $style?: RuleSet<object> }>`
  ${(props) => props.$style};
  position: absolute;
  top: 0;
  left: 0;
  padding: 0 !important;
`

const LazyImageContainer = styled(Container)<{ $style?: RuleSet<object> }>`
  position: relative;
  overflow: hidden;
  ${(props) => props.$style};
`

export default LazyImage
